// submitAuditReport.tsx
import FileUpload from "components/FileUpload";
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import React, { useState, useRef } from "react";
import { createAuditFollowupReport, getAuditLawImplementation } from "services/lawImplementationService";
import { LoadingIcon } from "utils/constant";
import { useTranslation } from "react-i18next";

interface SubmitAuditReportProps {
    request: any;
    isOpen: boolean;
    onClose: () => void;
    setLegalResearch: Function;
}

interface ReportRow {
    summary: string;
    file: File | null;
}

const emptyRow = (): ReportRow => ({ summary: "", file: null });

const SubmitAuditReport: React.FC<SubmitAuditReportProps> = ({
    request,
    isOpen,
    setLegalResearch,
    onClose,
}) => {
    const { t } = useTranslation();
    const contextData = useAuth();
    const { showToast } = useToast();
    const [rows, setRows] = useState<ReportRow[]>([emptyRow()]);
    const [submitting, setSubmitting] = useState(false);
    const modalContentRef = useRef<HTMLDivElement>(null);

    if (!isOpen) return null;

    const updateRow = (index: number, patch: Partial<ReportRow>) => {
        setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
    };

    const addRow = () => setRows((prev) => [...prev, emptyRow()]);

    const removeRow = (index: number) =>
        setRows((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const validRows = rows.filter((r) => r.summary.trim() || r.file);
        if (validRows.length === 0) {
            showToast(t("PleaseProvideAtLeastOneReport") || "Please provide at least one report.", "error");
            return;
        }
        if (validRows.some((r) => !r.summary.trim() || !r.file)) {
            showToast(t("EachReportNeedsSummaryAndFile") || "Each report needs a summary and a file.", "error");
            return;
        }

        setSubmitting(true);
        try {
            for (const row of validRows) {
                const data = new FormData();
                data.append("audit_followup", request.id);
                data.append("report_summary", row.summary);
                if (row.file) data.append("report_files", row.file);
                data.append("given_by", contextData?.user?.full_name || "");
                await createAuditFollowupReport(data);
            }

            // Refresh the audit record so newly created reports show up.
            try {
                if (request?.id) {
                    const refreshed = await getAuditLawImplementation(request.id);
                    setLegalResearch((prev: any) => ({ ...prev, ...refreshed }));
                }
            } catch {
                /* non-fatal: submission already succeeded */
            }

            showToast(
                t("ReportsSubmittedSuccess") || "Reports submitted successfully!",
                "success"
            );
            setRows([emptyRow()]);
            onClose();
        } catch (error) {
            console.error("Error submitting audit reports:", error);
            showToast(t("FailedToSubmitReports") || "Failed to submit reports.", "error");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-end p-4 z-50 overflow-hidden">
            <div
                ref={modalContentRef}
                className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 relative flex flex-col h-full my-auto overflow-y-auto pr-2 custom-scrollbar"
            >
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>

                <div className="mb-6">
                    <p className="text-[10px] uppercase tracking-widest text-amber-600 font-semibold">
                        {t("LawImplementationFollowUp") || "Law Implementation Follow-up"}
                    </p>
                    <h2 className="text-xl font-semibold text-gray-800">
                        {t("SubmitAuditReports") || "Submit Reports"}
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                        {t("SubmitMultipleReportsHint") || "You can add and submit multiple reports at once."}
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="flex flex-col flex-1">
                    <div className="flex-grow overflow-y-auto pr-2 custom-scrollbar space-y-5">
                        {rows.map((row, index) => (
                            <div key={index} className="rounded-lg border border-gray-200 p-4 relative">
                                <div className="flex items-center justify-between mb-3">
                                    <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#0A1D39]">
                                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#0A1D39] text-white text-xs font-bold">
                                            {index + 1}
                                        </span>
                                        {t("Report") || "Report"} {index + 1}
                                    </span>
                                    {rows.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => removeRow(index)}
                                            className="text-xs font-medium text-[#9B0505] hover:underline"
                                        >
                                            {t("Remove") || "Remove"}
                                        </button>
                                    )}
                                </div>

                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t("ReportSummary") || "Report Summary"}*
                                    </label>
                                    <textarea
                                        className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0A1D39] focus:border-transparent resize-y min-h-[90px]"
                                        placeholder={t("EnterReportSummaryPlaceholder") || "Enter report summary..."}
                                        value={row.summary}
                                        onChange={(e) => updateRow(index, { summary: e.target.value })}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t("ReportDocument") || "Report Document"}*
                                    </label>
                                    <FileUpload onChange={(file: any) => updateRow(index, { file })} />
                                </div>
                            </div>
                        ))}

                        <button
                            type="button"
                            onClick={addRow}
                            className="inline-flex items-center gap-2 text-sm font-semibold text-[#0A1D39] hover:underline"
                        >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                            </svg>
                            {t("AddAnotherReport") || "Add another report"}
                        </button>
                    </div>

                    <div className="flex space-x-4 pt-4 mt-4 border-t border-gray-200">
                        <button
                            type="button"
                            onClick={onClose}
                            className="w-1/2 bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-3 px-6 rounded-md"
                        >
                            {t("Cancel") || "Cancel"}
                        </button>
                        <button
                            type="submit"
                            disabled={submitting}
                            className="w-1/2 inline-flex items-center justify-center gap-2 bg-[#0A1D39] hover:bg-[#13294d] text-white font-bold py-3 px-6 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {submitting ? (
                                <>{LoadingIcon} {t("Submitting") || "Submitting..."}</>
                            ) : (
                                t("SubmitReports") || "Submit Reports"
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default SubmitAuditReport;
